import { useContext, useEffect } from "react";
import OrderRequestStateContext from "./OrderRequestStateContext";
import { requestOrderApi } from "../services/orderAPI";
import { wvusLogger } from "../helpers";

export function useRequestOrder() {
  const orderRequestCtx = useContext(OrderRequestStateContext);

  useEffect(() => {
    orderRequestCtx.setUniqueKey();
  }, []);

  useEffect(() => {
    orderRequestCtx.syncStorage();
  }, [orderRequestCtx.state.requests]);

  const hasNoOrderRequesting = () => {
    return orderRequestCtx.state.status !== "requesting";
  };

  const hasNoDupOrderRequestKey = (apiPayload) => {
    return !orderRequestCtx.state.requests.success.some((request) => {
      const foundDupRequest = apiPayload && request.key === orderRequestCtx.state.orderRequestKey;
      wvusLogger.log(
        `Duplicate Order Created with Payload: ${apiPayload} and request key: ${request.key}`,
        wvusLogger.getLogLevelNames().ERROR
      );
      return foundDupRequest;
    });
  };

  const duplicateOrderLookup = (apiPayload) => {
    if (!apiPayload || !Array.isArray(apiPayload.lineItems)) {
      console.error("Invalid apiPayload provided:", apiPayload);
      return null;
    }

    const payloadIdentifiers = apiPayload.lineItems.map(
      (item) => item.lineMeta?.wvus_product_child_identifier
    );

    return orderRequestCtx.state.requests.success.find((request) => {
      if (!request.payload || !Array.isArray(request.payload.lineItems)) {
        console.warn("Invalid request payload in state:", request);
        return false;
      }

      const requestIdentifiers = request.payload.lineItems.map(
        (item) => item.lineMeta?.wvus_product_child_identifier
      );

      // Check if any identifier from the payload exists in the request
      return payloadIdentifiers.some((id) => requestIdentifiers.includes(id));
    });
  };

  const hasNoDuplicateChildId = (apiPayload) => {
    const payloadIdentifiers = apiPayload.lineItems
      .map((item) => item.lineMeta?.wvus_product_child_identifier)
      .filter(Boolean); // Filter out null or undefined values

    if (!Array.isArray(orderRequestCtx.state.requests.success)) {
      console.warn(
        "orderRequestCtx.state.requests.success is not an array:",
        orderRequestCtx.state.requests.success
      );
      return true; // Allow order if state is inconsistent
    }

    const hasDuplicate = orderRequestCtx.state.requests.success.some((request) => {
      if (!request.payload || !Array.isArray(request.payload.lineItems)) {
        console.warn("Invalid request in success array:", request);
        return false;
      }

      const existingIdentifiers = request.payload.lineItems
        .map((item) => item.lineMeta?.wvus_product_child_identifier)
        .filter(Boolean); // Filter out invalid values

      // Check for duplicates
      return payloadIdentifiers.some((id) => {
        const isDuplicate = existingIdentifiers.includes(id);
        if (isDuplicate) {
          console.warn("Duplicate found for Child ID:", id);
        }
        return isDuplicate;
      });
    });

    return !hasDuplicate;
  };

  const startOrder = (apiPayload) => {
    return {
      isValid: () => {
        const isPayloadValid = [
          hasNoOrderRequesting,
          hasNoDupOrderRequestKey,
          hasNoDuplicateChildId
        ].every((fn) => {
          return fn(apiPayload);
        });
        return isPayloadValid;
      },
      requestOrder: async () => {
        orderRequestCtx.setOrderRequestStatus("requesting");

        try {
          const results = await requestOrderApi(apiPayload);
          const enhancedPayload = {
            ...apiPayload,
            orderId: results?.data?.orderId,
            orderRef: results?.data?.orderRef
          };
          orderRequestCtx.setOrderRequestComplete(enhancedPayload);
          return results;
        } catch (err) {
          orderRequestCtx.setOrderRequestStatus("pending");
          orderRequestCtx.setOrderRequestComplete(apiPayload, err);
          return Promise.reject(err);
        }
      }
    };
  };

  return {
    startOrder,
    orderRequestKey: orderRequestCtx.state.orderRequestKey,
    isRequesting: orderRequestCtx.state.status === "requesting",
    requestStatus: orderRequestCtx.state.status,
    orderRequestCtx,
    duplicateOrderLookup
  };
}

export default useRequestOrder;
